/*
 * The mark, set from the typeface's own outlines.
 *
 * ── Why this is not five letters in a span ──────────────────────────────────────────────────────
 *
 * The brand kit is explicit and the rule is narrow. Where the name is used *as a name* — a lockup,
 * a product label, a heading, the top of a product screen — it is set from Outfit's outlines with
 * the two e's joined by the accent bar, and **typing the letters is not the same thing and is not
 * correct in those positions**. In running prose the opposite holds: the plain word, because the
 * kit's minimum lockup height is 24px and a bar seven per cent of the cap height reads as a smudge
 * at body size rather than as the brand.
 *
 * Outlines rather than a web font, and this app loads none: the mark is right on the first paint
 * and stays right if a font never arrives. It is also the only way to draw the bar, which is not a
 * glyph in any face.
 *
 * ── Why this is in the design system when `nav-icons.tsx` argues icons are not ──────────────────
 *
 * That file's argument is about *meaning*: a navigation glyph is a claim about what a screen is,
 * and only the app knows that `standup` is a huddle. It holds, and this is its complement rather
 * than an exception. A brand mark carries no product meaning at all — it is one fixed shape, the
 * same shape in every app in the family, and the thing the app knows about it is not the drawing
 * but the name, which arrives as a prop.
 *
 * ── The name is a prop, and that is G-03 ────────────────────────────────────────────────────────
 *
 * Nothing here contains the name. It arrives as `label`, which callers take from the i18n
 * catalogue, because the product name is data and never an identifier — not in a filename, not in
 * a runtime string, and not in an `aria-label`. Which also makes these two components honest about
 * what they are: two fixed shapes and a bar, that happen to spell something.
 *
 * ── The bar's colour ────────────────────────────────────────────────────────────────────────────
 *
 * `--brand-bar`, which is deliberately not `--accent`. The product's accent is deep teal and is
 * spent on primary actions, active nav and focus; the bar is the *brand's* colour, it appears only
 * inside the mark, and it has to match the marketing site — the same mark in two places should not
 * be two different logos. `tokens.css` carries the reasoning and the value.
 */

import type { CSSProperties } from 'react';
import {
  ASSISTANT_ADVANCE,
  ASSISTANT_PATH,
  MARK_BASELINE_SHIFT,
  MARK_VIEWBOX_HEIGHT,
  MARK_VIEWBOX_TOP,
  SYMBOL_ADVANCE,
  SYMBOL_BAR,
  SYMBOL_PATH,
  WORD_ADVANCE,
  WORD_BAR,
  WORD_PATH,
} from './mark-data.js';
import type { MarkBar } from './mark-data.js';

export interface MarkProps {
  /**
   * What this spells, for anybody who cannot see it. From the catalogue — see the header.
   *
   * Omit it where the mark sits beside the word it stands for, as it does on a button that already
   * says the name: the mark then goes `aria-hidden`, because a control announced as "ee seeldo" is
   * one control read twice. This is `nav-icons.tsx`'s rule — the accessible name is always the
   * label beside the glyph — and the same reasoning applies to a mark.
   */
  readonly label?: string;
  readonly className?: string;
  /** Scales the mark against the text around it. 1 is "as tall as this line". */
  readonly scale?: number;
}

/* Outfit's baseline sits here, so the mark rests on the text baseline rather than floating. */
const INLINE: CSSProperties = {
  display: 'inline-block',
  verticalAlign: MARK_BASELINE_SHIFT,
  overflow: 'visible',
};

function set(path: string, advance: number, bar: MarkBar, { label, className, scale = 1 }: MarkProps) {
  /* Rounded: `2.203 * 100` is 220.29999999999998 in binary floating point. */
  const width = Math.round(advance * 100 * 100) / 100;
  const top = MARK_VIEWBOX_TOP;
  const height = MARK_VIEWBOX_HEIGHT;
  return (
    <svg
      viewBox={`0 ${top} ${width} ${height}`}
      width={`${advance * scale}em`}
      height={`${(height / 100) * scale}em`}
      className={className}
      {...(label === undefined
        ? { 'aria-hidden': true, focusable: false }
        : { role: 'img', 'aria-label': label })}
      style={INLINE}
    >
      <g transform="scale(0.1)">
        <path d={path} fill="currentColor" />
      </g>
      <rect
        x={bar.x0 * 100}
        y={-bar.y1 * 100}
        width={(bar.x1 - bar.x0) * 100}
        height={(bar.y1 - bar.y0) * 100}
        fill="var(--brand-bar)"
      />
    </svg>
  );
}

/**
 * The name, set as a name.
 *
 * For a lockup, a heading, or the top of a screen — never inside a sentence, where the plain word
 * is correct and this is a smudge.
 */
export function Wordmark(props: MarkProps) {
  return set(WORD_PATH, WORD_ADVANCE, WORD_BAR, props);
}

/**
 * The symbol alone — the two barred e's.
 *
 * The kit gives it three jobs: a favicon or app icon, an avatar, and the assistant's chip beside a
 * message. **A collapsed navigation rail is the first of those in everything but name**, which is
 * why the rail takes this rather than a shrunken wordmark: below the lockup's 24px minimum the word
 * stops being the brand and becomes small text.
 *
 * It is the assistant's mark too, and that is not a reuse. The kit says the two e's *are* the
 * assistant's symbol — which is also why the assistant's name is written plainly rather than as an
 * accented spelling of the product's.
 */
/**
 * The assistant's name, set as a name.
 *
 * Its own outline rather than the wordmark with an `o` after it: an `o` in whatever face the page
 * happens to be using, beside four letters set from Outfit, is visibly two typefaces in one word —
 * and this app loads no web font at all, so that is not a hypothetical.
 */
export function AssistantWord(props: MarkProps) {
  return set(ASSISTANT_PATH, ASSISTANT_ADVANCE, WORD_BAR, props);
}

export function BrandMark(props: MarkProps) {
  return set(SYMBOL_PATH, SYMBOL_ADVANCE, SYMBOL_BAR, props);
}
